import { usePasteTasks } from './paste/usePasteTasks';
import { useDuplicateTask } from './paste/useDuplicateTask';
import { usePasteWithCampaign } from './paste/usePasteWithCampaign';

/**
 * usePasteHandlers Hook (Barrel Export)
 * 
 * Handles pasting and duplicating tasks with nested sprint support.
 * This is the main entry point that combines three focused sub-hooks:
 * 
 * - usePasteTasks: Clipboard paste with tag/custom field merging
 * - useDuplicateTask: Task duplication with nested sprint cloning
 * - usePasteWithCampaign: Multi-campaign paste with position calculations
 * 
 * This file maintains backward compatibility - existing imports continue to work.
 * 
 * @param {Object} options
 * @param {Function} options.onCustomFieldConflict - Optional async callback called when a pasted
 *   custom field has the same name as an existing field but a different type.
 *   Signature: (conflict: { clipboardField, existingField, existingFieldNames }) => Promise<string|null>
 *   Returns the new name to use, or null to skip the field.
 */
export const usePasteHandlers = ({
  tasks,
  localSprint,
  setLocalSprint,
  nestedSprints,
  flowchartRef,
  onAddSprint,
  onUpdateSprint,
  onTrashSprint,
  recordAction,
  onCustomFieldConflict,
}) => {
  // Hook 1: Regular clipboard paste
  const { handlePasteTasks } = usePasteTasks({
    localSprint,
    setLocalSprint,
    flowchartRef,
    onAddSprint,
    onUpdateSprint,
    onTrashSprint,
    recordAction,
    onCustomFieldConflict,
  });

  // Hook 2: Duplicate task with nested sprint support
  const { handleDuplicateTaskWithNested } = useDuplicateTask({
    tasks,
    nestedSprints,
    localSprint,
    onAddSprint,
    onUpdateSprint,
    setLocalSprint,
  });

  // Hook 3: Multi-campaign paste
  const { handlePasteWithCampaign } = usePasteWithCampaign({
    setLocalSprint,
    localSprint,
    onAddSprint,
    onUpdateSprint,
    onTrashSprint,
    flowchartRef,
    recordAction,
    onCustomFieldConflict,
  });

  return {
    handlePasteTasks,
    handleDuplicateTaskWithNested,
    handlePasteWithCampaign,
  };
};

export default usePasteHandlers;
